/**
 * Readers for values kept in per-user storage (see userStorage). Storage
 * outlives builds, so each reader validates the stored text against
 * today's options and yields the caller's default when it doesn't fit.
 */

/** The stored text as JSON; undefined when absent or unparseable. */
export function parseStoredJson(raw: string | null): unknown {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** A parsed JSON value that is a plain object (not null, not an array). */
export function isStoredObject(
  value: unknown,
): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  return !Array.isArray(value);
}

/** A stored JSON array narrowed to the values that are still options. */
export function parseAllowedList<T extends string>(
  raw: string | null,
  allowed: ReadonlyArray<T>,
): T[] {
  const parsed = parseStoredJson(raw);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((v): v is T => allowed.includes(v as T));
}

/** A stored single choice, when it is still one of the options. */
export function parseOneOf<T extends string>(
  raw: string | null,
  allowed: ReadonlyArray<T>,
): T | undefined {
  return allowed.includes(raw as T) ? (raw as T) : undefined;
}

/** A stored number clamped into [min, max]; undefined when not numeric. */
export function parseBoundedNumber(
  raw: string | null,
  min: number,
  max: number,
): number | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(max, Math.max(min, n));
}
